import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useAppData, UpdateWorkspaceGeneralDocument } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";
import { createEditSessionGate } from "#app/lib/analytics/edit-session";

// The name autosaves per typing pause; count one rename per session.
const nameSession = createEditSessionGate(5 * 60_000);

const trackWorkspaceUpdate = (field: "name" | "avatarColor") =>
  trackEvent({
    context: AnalyticsContext.Settings,
    event: SettingsEvent.Update,
    label: "workspace",
    data: { fields: [field] },
  });

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * The workspace-settings page's data layer: the active workspace plus the
 * General-group saves. The backend gates `updateWorkspace` on `create.roles`
 * scoped to the actor's own workspace; the global Apollo error link toasts a
 * denial, so no client-side pre-check is needed here.
 */
export function useWorkspaceSettings() {
  const { activeWorkspace } = useWorkspace();
  // `activeWorkspace` is provider React state parsed at boot, not a live
  // cache read — a successful save must refetch the provider so the
  // workspace selector and this page pick the new name up.
  const { refetch: refetchAppData } = useAppData();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const [updateWorkspaceMutation] = useMutation(UpdateWorkspaceGeneralDocument);

  // Saves arrive keystroke-debounced from the auto-saving name field, so the
  // success toast fires once per editing session, not once per typing pause.
  // Errors still toast.
  const updateName = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!workspaceId || !trimmed || trimmed === activeWorkspace?.name) return;
      try {
        const { data } = await updateWorkspaceMutation({
          variables: { id: workspaceId, input: { name: trimmed } },
        });
        // errorPolicy "all": a refused save resolves with no data, and the
        // error link has already toasted why.
        if (!data) return;
        if (nameSession(workspaceId)) {
          trackWorkspaceUpdate("name");
          toastManager.add({ title: "Workspace name saved", type: "success" });
        }
        await refetchAppData();
      } catch (err) {
        toastMutationError(err, "Failed to rename workspace");
      }
    },
    [
      workspaceId,
      activeWorkspace?.name,
      updateWorkspaceMutation,
      refetchAppData,
    ],
  );

  // No success toast: a swatch press paints the tile itself — the feedback
  // is the tile.
  const updateAvatarColor = useCallback(
    async (color: string) => {
      if (!workspaceId || color === activeWorkspace?.avatarColor) return;
      try {
        await updateWorkspaceMutation({
          variables: { id: workspaceId, input: { avatarColor: color } },
        });
        trackWorkspaceUpdate("avatarColor");
        await refetchAppData();
      } catch (err) {
        toastMutationError(err, "Failed to update workspace color");
      }
    },
    [
      workspaceId,
      activeWorkspace?.avatarColor,
      updateWorkspaceMutation,
      refetchAppData,
    ],
  );

  return { workspace: activeWorkspace, updateName, updateAvatarColor };
}
